import React, { createContext, useContext, ReactNode, useEffect, useState } from "react";
import { Theme } from "src/contexts/ThemeContext";
import { EUrlParams } from "src/types";
import { getSafeApiUrl } from "src/utils/urlValidator";

interface UrlParamsContextType {
  theme: Theme;
  storedDataKey: string;
  language: string;
  currencyId: string;
  paymentRouteId: string;
  /** Resolved backend GraphQL URL — derived from `?exchange=` (or
   *  the legacy `?apiUrl=` fallback). Empty when nothing resolves. */
  apiUrl: string;
  /** Raw exchange name from the URL — surfaced for downstream code
   *  that needs to forward it to the backend (e.g. webhook payloads). */
  exchange: string;
  walletId: string;
}

const UrlParamsContext = createContext<UrlParamsContextType | undefined>(undefined);

interface UrlParamsProviderProps {
  children: ReactNode;
}

export const UrlParamsProvider: React.FC<UrlParamsProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(Theme.Light);
  const [storedDataKey, setStoredDataKey] = useState<string>("");
  const [language, setLanguage] = useState<string>("en");
  const [currencyId, setCurrencyId] = useState<string>("");
  const [paymentRouteId, setPaymentRouteId] = useState<string>("");
  const [apiUrl, setApiUrl] = useState<string>("");
  const [exchange, setExchange] = useState<string>("");
  const [walletId, setWalletId] = useState<string>("");

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const themeFromUrl = searchParams.get(EUrlParams.Theme);
    const storedDataKeyFromUrl = searchParams.get(EUrlParams.StoredDataKey);
    const languageFromUrl = searchParams.get(EUrlParams.Language);
    const currencyFromUrl = searchParams.get(EUrlParams.CurrencyId);
    const paymentRouteIdFromUrl = searchParams.get(EUrlParams.PaymentRouteId);
    const exchangeFromUrl = searchParams.get(EUrlParams.Exchange);
    // ApiUrl is the legacy fallback — kept until every host migrates
    // to `?exchange=<name>`. The resolver in urlValidator picks the
    // exchange map first, falls back to the legacy whitelist.
    const apiUrlFromUrl = searchParams.get(EUrlParams.ApiUrl);
    const walletIdFromUrl = searchParams.get(EUrlParams.WalletId);

    setStoredDataKey(storedDataKeyFromUrl ?? "");
    setLanguage(languageFromUrl ?? "en");
    setCurrencyId(currencyFromUrl ?? "");
    setPaymentRouteId(paymentRouteIdFromUrl ?? "");
    setExchange(exchangeFromUrl ?? "");
    setApiUrl(getSafeApiUrl(exchangeFromUrl, apiUrlFromUrl) ?? "");
    setWalletId(walletIdFromUrl ?? "");
    setTheme(themeFromUrl && [Theme.Light, Theme.Dark].includes(themeFromUrl as Theme) ? (themeFromUrl as Theme) : Theme.Light);
  }, []);

  const urlParams = {
    theme,
    storedDataKey,
    language,
    currencyId,
    paymentRouteId,
    apiUrl,
    exchange,
    walletId,
  };

  return <UrlParamsContext.Provider value={urlParams}>{children}</UrlParamsContext.Provider>;
};

export const useUrlParamsContext = (): UrlParamsContextType => {
  const context = useContext(UrlParamsContext);

  if (context === undefined) {
    throw new Error("useUrlParamsContext must be used within a UrlParamsProvider");
  }

  return context;
};
